import type { UserState } from "$lib/stores/userStore"
import type { Competition } from "$lib/types/competition"
import { generateHmac } from "$lib/utils/encryption"
import { PUBLIC_HMAC_SECRET_KEY } from "$env/static/public"

interface ReferralParams {
  user: UserState["user"]
  competition: Competition
  redirectUrl?: string
}

export async function getParentUrl(): Promise<string> {
  return new Promise((resolve) => {
    const messageHandler = (event: MessageEvent) => {
      if (event.data.type === "PARENT_URL_RESPONSE") {
        window.removeEventListener("message", messageHandler)
        resolve(event.data.url)
      }
    }
    window.addEventListener("message", messageHandler)
    window.parent.postMessage({ type: "REQUEST_PARENT_URL" }, "*")
  })
}

export async function generateReferralLink({
  user,
  competition,
  redirectUrl = window.location.href,
}: ReferralParams): Promise<string> {
  if (!user) {
    throw new Error("User info is missing")
  }

  let finalRedirectUrl = redirectUrl

  if (window.parent !== window) {
    // Check if in iframe
    try {
      finalRedirectUrl = await getParentUrl()
    } catch (error) {
      console.error("Error getting parent URL:", error)
    }
  }

  const cleanUrl = new URL(finalRedirectUrl)
  finalRedirectUrl = `${cleanUrl.origin}${cleanUrl.pathname.replace(/\/$/, "")}`

  const shouldUseCname =
    competition.cname &&
    competition?.cnameDetails?.status?.toLowerCase() === "active"

  const requestBody = JSON.stringify({
    participant_name: user.name,
    participant_email: user.email,
    competition_id: competition.id,
    // cname: shouldUseCname ? competition.cname : null,
    cname: competition.cname,
    redirect_url: finalRedirectUrl,
  })

  // Generate HMAC signature if PUBLIC_HMAC_SECRET_KEY is available
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  }

  if (PUBLIC_HMAC_SECRET_KEY) {
    const hmacSignature = await generateHmac(
      requestBody,
      PUBLIC_HMAC_SECRET_KEY,
    )
    headers["x-hmac-signature"] = hmacSignature
  }

  const response = await fetch("/api/create-referral", {
    method: "POST",
    headers,
    body: requestBody,
  })

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.error || "Failed to generate referral link")
  }

  const data = await response.json()
  return data.referralLink
}
